import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { useReducedMotion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { Fragment, type PropsWithChildren, useState } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import type { ImageProps } from '@/app/components/Image';
import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';
import { type VideoProps } from '@/app/components/Video/types/VideoProps';
import Video from '@/app/components/Video/Video';

interface Photo extends ImageProps {
  caption: {
    name: string;
    quote: MessageKey;
    title: MessageKey;
  };
  video?: Partial<VideoProps>;
}

export interface CareersPhotoWallProps {
  readonly photos: Photo[];
}

const CareersPhotoWall = ({
  children,
  photos,
}: PropsWithChildren<CareersPhotoWallProps>) => {
  const t = useTranslations();
  const prefersReducedMotion = useReducedMotion();
  const [playVideo, setPlayVideo] = useState<boolean>(false);

  return (
    <LayoutGrid rowGap={64} data-cy="photo-wall-grid">
      <div
        data-cy="photo-wall-intro"
        className="col-main lg:col-start-15 lg:col-end-main lg:row-start-1 lg:row-end-3"
      >
        {children}
      </div>

      {photos.map(({ video, caption, ...imageProps }, i) => (
        <Fragment key={imageProps.src}>
          <div
            className={cx('relative col-full lg:col-main', {
              'min-h-[36rem] lg:col-end-13 lg:row-start-1 lg:row-end-4':
                i === 0,
              'col-start-4 min-h-[30rem] lg:col-start-15 lg:col-end-main lg:row-start-3 lg:row-end-5':
                i === 1,
              'col-end-8 min-h-[30rem] md:col-start-full md:col-end-13 lg:col-start-5 lg:col-end-13 lg:row-start-5 lg:row-end-7':
                i === 2,
            })}
            data-cy="photo-wall-item-media"
          >
            {video && !prefersReducedMotion ? (
              <div data-cy="photo-wall-video">
                <Motion
                  onViewportEnter={() => setPlayVideo(true)}
                  onViewportLeave={() => setPlayVideo(false)}
                >
                  <Video
                    type="video/mp4"
                    contentTitle="pdp_video"
                    controls={false}
                    loop={false}
                    location="careers-photowall"
                    playsInline={true}
                    muted={true}
                    autoPlay={true}
                    {...video}
                    playVideo={playVideo}
                    altImage={{
                      className: 'object-cover',
                      fill: true,
                      ...imageProps,
                    }}
                  />
                </Motion>
              </div>
            ) : (
              <Image
                className="object-cover"
                data-cy="photo-wall-image"
                fill
                {...imageProps}
              />
            )}
          </div>
          <div
            className={cx('col-main', {
              'pb-12 lg:col-start-main lg:col-end-11 lg:row-start-4 lg:pb-8 xl:col-end-8':
                i === 0,
              'col-start-4 pb-12 lg:col-start-15 lg:col-end-main lg:row-start-5 lg:pb-8':
                i === 1,
              'col-end-8 md:col-end-13 lg:col-start-5 lg:row-start-7': i === 2,
            })}
            data-cy="photo-wall-item-caption"
          >
            <blockquote>
              <Typography className="mb-4 before:content-[open-quote] after:content-[close-quote]">
                <RichText messageKey={caption.quote} />
              </Typography>
            </blockquote>
            <p>
              <Typography
                className="block"
                Element="span"
                variant="body-small"
                weight="bold"
              >
                {caption.name}
              </Typography>
              <Typography
                className="block"
                Element="span"
                variant="body-small"
                weight="medium"
              >
                {t(caption.title)}
              </Typography>
            </p>
          </div>
        </Fragment>
      ))}
    </LayoutGrid>
  );
};

export default CareersPhotoWall;
